import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  ListItemText,
  Checkbox,
  OutlinedInput,
  InputAdornment,
  TextField,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RestartAltIcon from "@mui/icons-material/RestartAlt"; // Import reset icon

const FileFilters = ({
  availableFiles = [],
  brandList = [],
  categoryList = [],
  product_categoryList = [],
  sourcesList = [],
  tocList = [],
  yearList = [],
  onFilterChange,
  datasourceValue,
  selectedDataSources,
  showHeader = true,
}) => {
  // console.log("Datasource value1111111111111111111111111:", selectedDataSources,datasourceValue);
  // State variables for filter values
  const [selectedFiles, setSelectedFiles] = useState(["All"]);
  const [selectedCategory, setSelectedCategory] = useState(["All"]);
  const [selectedProductCategory, setSelectedProductCategory] = useState([
    "All",
  ]);
  const [selectedBrands, setSelectedBrands] = useState(["All"]);
  const [selectedSource, setSelectedSource] = useState(["All"]);
  const [selectedYear, setSelectedYear] = useState(["All"]);
  const [selectedTableOfContent, setSelectedTableOfContent] = useState(["All"]);

  // Search states for filter dropdowns
  const [fileSearchQuery, setFileSearchQuery] = useState(""); // Add missing file search state
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const [productCategorySearchQuery, setProductCategorySearchQuery] =
    useState("");
  const [brandsSearchQuery, setBrandsSearchQuery] = useState("");
  const [sourceSearchQuery, setSourceSearchQuery] = useState("");
  const [yearSearchQuery, setYearSearchQuery] = useState("");
  const [tocSearchQuery, setTocSearchQuery] = useState("");

  // Clear search handlers
  const clearFileSearch = () => setFileSearchQuery(""); // Add missing clear function
  const clearCategorySearch = () => setCategorySearchQuery("");
  const clearProductCategorySearch = () => setProductCategorySearchQuery("");
  const clearBrandsSearch = () => setBrandsSearchQuery("");
  const clearSourceSearch = () => setSourceSearchQuery("");
  const clearYearSearch = () => setYearSearchQuery("");
  const clearTocSearch = () => setTocSearchQuery("");

  // Filter functions
  const filteredCategories = categorySearchQuery
    ? [
        "All",
        ...categoryList.filter(
          (cat) =>
            cat !== "All" &&
            cat.toLowerCase().includes(categorySearchQuery.toLowerCase())
        ),
      ]
    : ["All", ...categoryList];

  const filteredProductCategories = productCategorySearchQuery
    ? [
        "All",
        ...product_categoryList.filter(
          (cat) =>
            cat !== "All" &&
            cat.toLowerCase().includes(productCategorySearchQuery.toLowerCase())
        ),
      ]
    : ["All", ...product_categoryList];

  const filteredBrands = brandsSearchQuery
    ? [
        "All",
        ...brandList.filter(
          (brand) =>
            brand !== "All" &&
            brand.toLowerCase().includes(brandsSearchQuery.toLowerCase())
        ),
      ]
    : ["All", ...brandList];

  const filteredSources = sourceSearchQuery
    ? [
        "All",
        ...sourcesList.filter(
          (source) =>
            source !== "All" &&
            source.toLowerCase().includes(sourceSearchQuery.toLowerCase())
        ),
      ]
    : ["All", ...sourcesList];

  const filteredYears = yearSearchQuery
    ? [
        "All",
        ...yearList.filter(
          (year) =>
            year !== "All" &&
            year.toLowerCase().includes(yearSearchQuery.toLowerCase())
        ),
      ]
    : ["All", ...yearList];

  const filteredTableOfContents = tocSearchQuery
    ? [
        "All",
        ...tocList.filter(
          (toc) =>
            toc !== "All" &&
            toc.toLowerCase().includes(tocSearchQuery.toLowerCase())
        ),
      ]
    : tocList;

  // Add filtered files function
  const filteredAvailableFiles = fileSearchQuery
    ? [
        "All",
        ...availableFiles.filter((file) =>
          file.toLowerCase().includes(fileSearchQuery.toLowerCase())
        ),
      ]
    : ["All", ...availableFiles];

  // Handlers for dropdown changes
  const handleFilesChange = (event) => {
    const value = event.target.value;
    let newSelected;

    if (value.includes("All")) {
      if (!selectedFiles.includes("All")) {
        // User clicked "All" when it wasn't selected - select only "All"
        newSelected = ["All"];
      } else {
        // User clicked something else when "All" was already selected
        newSelected = value.filter((option) => option !== "All");
        if (newSelected.length === 0) {
          newSelected = ["All"];
        }
      }
    } else {
      // Normal selection without "All"
      newSelected = value;
      if (newSelected.length === 0) {
        newSelected = ["All"];
      }
    }

    setSelectedFiles(newSelected);
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    let newSelected;

    if (value.includes("All")) {
      if (!selectedCategory.includes("All")) {
        newSelected = ["All"];
      } else {
        newSelected = value.filter((option) => option !== "All");
        if (newSelected.length === 0) {
          newSelected = ["All"];
        }
      }
    } else {
      newSelected = value;
      if (newSelected.length === 0) {
        newSelected = ["All"];
      }
    }

    setSelectedCategory(newSelected);
  };

  const handleProductCategoryChange = (event) => {
    const value = event.target.value;
    let newSelected;

    if (value.includes("All")) {
      if (!selectedProductCategory.includes("All")) {
        newSelected = ["All"];
      } else {
        newSelected = value.filter((option) => option !== "All");
        if (newSelected.length === 0) {
          newSelected = ["All"];
        }
      }
    } else {
      newSelected = value;
      if (newSelected.length === 0) {
        newSelected = ["All"];
      }
    }

    setSelectedProductCategory(newSelected);
  };

  const handleBrandsChange = (event) => {
    const value = event.target.value;
    let newSelected;

    if (value.includes("All")) {
      if (!selectedBrands.includes("All")) {
        newSelected = ["All"];
      } else {
        newSelected = value.filter((option) => option !== "All");
        if (newSelected.length === 0) {
          newSelected = ["All"];
        }
      }
    } else {
      newSelected = value;
      if (newSelected.length === 0) {
        newSelected = ["All"];
      }
    }

    setSelectedBrands(newSelected);
  };

  const handleSourceChange = (event) => {
    const value = event.target.value;
    let newSelected;

    if (value.includes("All")) {
      if (!selectedSource.includes("All")) {
        newSelected = ["All"];
      } else {
        newSelected = value.filter((option) => option !== "All");
        if (newSelected.length === 0) {
          newSelected = ["All"];
        }
      }
    } else {
      newSelected = value;
      if (newSelected.length === 0) {
        newSelected = ["All"];
      }
    }

    setSelectedSource(newSelected);
  };

  const handleYearChange = (event) => {
    const value = event.target.value;
    let newSelected;

    if (value.includes("All")) {
      if (!selectedYear.includes("All")) {
        newSelected = ["All"];
      } else {
        newSelected = value.filter((option) => option !== "All");
        if (newSelected.length === 0) {
          newSelected = ["All"];
        }
      }
    } else {
      newSelected = value;
      if (newSelected.length === 0) {
        newSelected = ["All"];
      }
    }

    setSelectedYear(newSelected);
  };

  const handleTableOfContentChange = (event) => {
    const value = event.target.value;
    let newSelected;

    if (value.includes("All")) {
      if (!selectedTableOfContent.includes("All")) {
        newSelected = ["All"];
      } else {
        newSelected = value.filter((option) => option !== "All");
        if (newSelected.length === 0) {
          newSelected = ["All"];
        }
      }
    } else {
      newSelected = value;
      if (newSelected.length === 0) {
        newSelected = ["All"];
      }
    }

    setSelectedTableOfContent(newSelected);
  };

  // Build filter payload function
  const buildFilterPayload = () => {
    const filters = {};

    // Only add filters that don't have "All" selected
    // For each filter, if it's not "All", add the array of selected values
    // let user_id = localStorage.getItem("user_id");
    // if (user_id) {
    //   filters.user_id = user_id;
    // }

    if (
      selectedFiles &&
      !selectedFiles.includes("All") &&
      selectedFiles.length > 0
    ) {
      filters.filename = selectedFiles;
    }

    if (!selectedCategory.includes("All") && selectedCategory.length > 0) {
      filters.category = selectedCategory;
    }

    if (
      !selectedProductCategory.includes("All") &&
      selectedProductCategory.length > 0
    ) {
      filters.product_category = selectedProductCategory;
    }

    if (!selectedBrands.includes("All") && selectedBrands.length > 0) {
      filters.brands = selectedBrands;
    }

    if (!selectedSource.includes("All") && selectedSource.length > 0) {
      filters.source = selectedSource;
    }

    if (!selectedYear.includes("All") && selectedYear.length > 0) {
      filters.year = selectedYear;
    }

    if (
      !selectedTableOfContent.includes("All") &&
      selectedTableOfContent.length > 0
    ) {
      filters.table_of_content = selectedTableOfContent;
    }

    return { filters };
  };

  // Track when dropdown is open/closed and if filters need to be applied
  // Track if this is the initial load to prevent unwanted resets
  const isInitialLoadRef = useRef(true);
  const [anyDropdownOpen, setAnyDropdownOpen] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const previousDropdownState = React.useRef(false);

  // Effect to track dropdown closing and set flag to apply filters
  useEffect(() => {
    // Only set the flag to true when dropdown was previously open and is now closed
    if (previousDropdownState.current && !anyDropdownOpen) {
      setApplyFilters(true);
    }
    // Update the previous state for the next render
    previousDropdownState.current = anyDropdownOpen;
  }, [anyDropdownOpen]);

  // Separate effect to handle filter application to avoid infinite loops
  useEffect(() => {
    if (applyFilters && onFilterChange) {
      const payload = buildFilterPayload();
      onFilterChange(payload);
      setApplyFilters(false); // Reset flag after applying
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilters]);

  // Only reset filters when explicitly changing to circana datasource, not during normal interactions
  useEffect(() => {
    if (datasourceValue === "circana" && !isInitialLoadRef.current) {
      handleResetFilters(); // Reset filters when datasource changes to 'circana'
    }
    isInitialLoadRef.current = false; // Mark that initial load is complete
  }, [datasourceValue]);

  // Helper function to create a search field for dropdowns
  const createSearchField = (value, onChange, placeholder, clearSearch) => (
    <Box
      sx={{
        p: 1,
        position: "sticky",
        top: 0,
        bgcolor: "background.paper",
        zIndex: 1,
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  clearSearch();
                }}
                edge="end"
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: {
            fontFamily: "'Poppins', sans-serif",
            fontSize: "14px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E2E8F0",
            },
          },
        }}
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        sx={{ zIndex: 2, position: "relative" }}
      />
    </Box>
  );

  // Check if any filter has non-default selection
  const hasFilterSelections = () => {
    return (
      (selectedFiles.length > 0 && !selectedFiles.includes("All")) ||
      (selectedCategory.length > 0 && !selectedCategory.includes("All")) ||
      (selectedProductCategory.length > 0 &&
        !selectedProductCategory.includes("All")) ||
      (selectedBrands.length > 0 && !selectedBrands.includes("All")) ||
      (selectedSource.length > 0 && !selectedSource.includes("All")) ||
      (selectedYear.length > 0 && !selectedYear.includes("All")) ||
      (selectedTableOfContent.length > 0 &&
        !selectedTableOfContent.includes("All"))
    );
  };

  // Function to reset all filters to default "All" value
  const handleResetFilters = () => {
    setSelectedFiles(["All"]);
    setSelectedCategory(["All"]);
    setSelectedProductCategory(["All"]);
    setSelectedBrands(["All"]);
    setSelectedSource(["All"]);
    setSelectedYear(["All"]);
    setSelectedTableOfContent(["All"]);

    // Also clear all search queries
    setFileSearchQuery("");
    setCategorySearchQuery("");
    setProductCategorySearchQuery("");
    setBrandsSearchQuery("");
    setSourceSearchQuery("");
    setYearSearchQuery("");
    setTocSearchQuery("");

    // Trigger filter application immediately after reset
    setApplyFilters(true);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: showHeader ? 1 : 0.5,
        }}
      >
        {showHeader ? (
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              color: "#1B2559",
              fontWeight: 600,
            }}
          >
            Document Datasource Filters
          </Typography>
        ) : (
          <Box />
        )}

        {/* Reset button - only show if some filter is selected */}
        {hasFilterSelections() && (
          <Tooltip title="Reset filters" placement="top">
            <IconButton
              size="small"
              onClick={handleResetFilters}
              sx={{
                color: "#1976d2",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Files dropdown */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel
          id="files-select-label"
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "14px",
            color: "#1B2559",
          }}
        >
          Files
        </InputLabel>
        <Select
          labelId="files-select-label"
          id="files-select"
          multiple
          value={selectedFiles}
          onChange={handleFilesChange}
          onOpen={() => setAnyDropdownOpen(true)}
          onClose={() => setAnyDropdownOpen(false)}
          input={<OutlinedInput label="Files" />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  title={value}
                  sx={{ fontFamily: "'Poppins', sans-serif" }}
                />
              ))}
            </Box>
          )}
          sx={{
            fontFamily: "'Poppins', sans-serif",
            borderRadius: "8px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E2E8F0",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 320,
                width: {
                  xs: "calc(100vw - 48px)",
                  sm: "280px",
                },
                maxWidth: {
                  xs: "calc(100vw - 48px)",
                  sm: "280px",
                },
                "& .MuiMenuItem-root": {
                  minHeight: 40,
                },
              },
            },
          }}
        >
          {createSearchField(
            fileSearchQuery,
            (e) => setFileSearchQuery(e.target.value),
            "Search files...",
            clearFileSearch
          )}

          <Divider sx={{ my: 0.5 }} />
          {/* Update this line to use filteredAvailableFiles */}
          {filteredAvailableFiles.map((file) => (
            <MenuItem
              key={file}
              value={file}
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: "14px",
              }}
            >
              <Checkbox checked={selectedFiles.indexOf(file) > -1} />
              <ListItemText
                primary={file}
                primaryTypographyProps={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "14px",
                  fontWeight: file === "All" ? 600 : 400,
                  noWrap: true,
                  title: file,
                  sx: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Category dropdown */}
      {filteredCategories.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel
            id="category-select-label"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px",
              color: "#1B2559",
            }}
          >
            Category
          </InputLabel>
          <Select
            labelId="category-select-label"
            id="category-select"
            multiple
            value={selectedCategory}
            onChange={handleCategoryChange}
            onOpen={() => setAnyDropdownOpen(true)}
            onClose={() => setAnyDropdownOpen(false)}
            input={<OutlinedInput label="Category" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    title={value}
                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                  />
                ))}
              </Box>
            )}
            sx={{
              fontFamily: "'Poppins', sans-serif",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E2E8F0",
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {createSearchField(
              categorySearchQuery,
              (e) => setCategorySearchQuery(e.target.value),
              "Search categories...",
              clearCategorySearch
            )}

            <Divider sx={{ my: 0.5 }} />
            {filteredCategories.map((category) => (
              <MenuItem
                key={category}
                value={category}
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "14px",
                }}
              >
                <Checkbox checked={selectedCategory.indexOf(category) > -1} />
                <ListItemText
                  primary={category}
                  primaryTypographyProps={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "14px",
                    fontWeight: category === "All" ? 600 : 400,
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {/* Product Category dropdown */}
      {filteredProductCategories.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel
            id="product-category-select-label"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px",
              color: "#1B2559",
            }}
          >
            Product Category
          </InputLabel>
          <Select
            labelId="product-category-select-label"
            id="product-category-select"
            multiple
            value={selectedProductCategory}
            onChange={handleProductCategoryChange}
            onOpen={() => setAnyDropdownOpen(true)}
            onClose={() => setAnyDropdownOpen(false)}
            input={<OutlinedInput label="Product Category" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    title={value}
                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                  />
                ))}
              </Box>
            )}
            sx={{
              fontFamily: "'Poppins', sans-serif",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E2E8F0",
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {createSearchField(
              productCategorySearchQuery,
              (e) => setProductCategorySearchQuery(e.target.value),
              "Search product categories...",
              clearProductCategorySearch
            )}

            <Divider sx={{ my: 0.5 }} />
            {filteredProductCategories.map((category) => (
              <MenuItem
                key={category}
                value={category}
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "14px",
                }}
              >
                <Checkbox
                  checked={selectedProductCategory.indexOf(category) > -1}
                />
                <ListItemText
                  primary={category}
                  primaryTypographyProps={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "14px",
                    fontWeight: category === "All" ? 600 : 400,
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {/* Brands dropdown */}
      {filteredBrands.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel
            id="brands-select-label"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px",
              color: "#1B2559",
            }}
          >
            Brands
          </InputLabel>
          <Select
            labelId="brands-select-label"
            id="brands-select"
            multiple
            value={selectedBrands}
            onChange={handleBrandsChange}
            onOpen={() => setAnyDropdownOpen(true)}
            onClose={() => setAnyDropdownOpen(false)}
            input={<OutlinedInput label="Brands" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    title={value}
                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                  />
                ))}
              </Box>
            )}
            sx={{
              fontFamily: "'Poppins', sans-serif",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E2E8F0",
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {createSearchField(
              brandsSearchQuery,
              (e) => setBrandsSearchQuery(e.target.value),
              "Search brands...",
              clearBrandsSearch
            )}

            <Divider sx={{ my: 0.5 }} />
            {filteredBrands.map((brand) => (
              <MenuItem
                key={brand}
                value={brand}
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "14px",
                }}
              >
                <Checkbox checked={selectedBrands.indexOf(brand) > -1} />
                <ListItemText
                  primary={brand}
                  primaryTypographyProps={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "14px",
                    fontWeight: brand === "All" ? 600 : 400,
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {/* Source dropdown */}
      {filteredSources.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel
            id="source-select-label"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px",
              color: "#1B2559",
            }}
          >
            Source
          </InputLabel>
          <Select
            labelId="source-select-label"
            id="source-select"
            multiple
            value={selectedSource}
            onChange={handleSourceChange}
            onOpen={() => setAnyDropdownOpen(true)}
            onClose={() => setAnyDropdownOpen(false)}
            input={<OutlinedInput label="Source" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    title={value}
                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                  />
                ))}
              </Box>
            )}
            sx={{
              fontFamily: "'Poppins', sans-serif",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E2E8F0",
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {createSearchField(
              sourceSearchQuery,
              (e) => setSourceSearchQuery(e.target.value),
              "Search sources...",
              clearSourceSearch
            )}

            <Divider sx={{ my: 0.5 }} />
            {filteredSources.map((source) => (
              <MenuItem
                key={source}
                value={source}
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "14px",
                }}
              >
                <Checkbox checked={selectedSource.indexOf(source) > -1} />
                <ListItemText
                  primary={source}
                  primaryTypographyProps={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "14px",
                    fontWeight: source === "All" ? 600 : 400,
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {/* Year dropdown */}
      {filteredYears.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel
            id="year-select-label"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px",
              color: "#1B2559",
            }}
          >
            Year
          </InputLabel>
          <Select
            labelId="year-select-label"
            id="year-select"
            multiple
            value={selectedYear}
            onChange={handleYearChange}
            onOpen={() => setAnyDropdownOpen(true)}
            onClose={() => setAnyDropdownOpen(false)}
            input={<OutlinedInput label="Year" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    title={value}
                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                  />
                ))}
              </Box>
            )}
            sx={{
              fontFamily: "'Poppins', sans-serif",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E2E8F0",
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {createSearchField(
              yearSearchQuery,
              (e) => setYearSearchQuery(e.target.value),
              "Search years...",
              clearYearSearch
            )}

            <Divider sx={{ my: 0.5 }} />
            {filteredYears.map((year) => (
              <MenuItem
                key={year}
                value={year}
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "14px",
                }}
              >
                <Checkbox checked={selectedYear.indexOf(year) > -1} />
                <ListItemText
                  primary={year}
                  primaryTypographyProps={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "14px",
                    fontWeight: year === "All" ? 600 : 400,
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {/* Table of Contents dropdown */}
      {filteredTableOfContents.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel
            id="toc-select-label"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px",
              color: "#1B2559",
            }}
          >
            Table of Contents
          </InputLabel>
          <Select
            labelId="toc-select-label"
            id="toc-select"
            multiple
            value={selectedTableOfContent}
            onChange={handleTableOfContentChange}
            onOpen={() => setAnyDropdownOpen(true)}
            onClose={() => setAnyDropdownOpen(false)}
            input={<OutlinedInput label="Table of Contents" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    title={value}
                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                  />
                ))}
              </Box>
            )}
            sx={{
              fontFamily: "'Poppins', sans-serif",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E2E8F0",
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {createSearchField(
              tocSearchQuery,
              (e) => setTocSearchQuery(e.target.value),
              "Search table of contents...",
              clearTocSearch
            )}

            <Divider sx={{ my: 0.5 }} />
            {filteredTableOfContents.map((toc) => (
              <MenuItem
                key={toc}
                value={toc}
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "14px",
                }}
              >
                <Checkbox checked={selectedTableOfContent.indexOf(toc) > -1} />
                <ListItemText
                  primary={toc}
                  primaryTypographyProps={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "14px",
                    fontWeight: toc === "All" ? 600 : 400,
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </>
  );
};

FileFilters.propTypes = {
  availableFiles: PropTypes.array,
  brandList: PropTypes.array,
  categoryList: PropTypes.array,
  product_categoryList: PropTypes.array,
  sourcesList: PropTypes.array,
  tocList: PropTypes.array,
  yearList: PropTypes.array,
  onFilterChange: PropTypes.func,
  datasourceValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  selectedDataSources: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  showHeader: PropTypes.bool,
};

export default FileFilters;
